import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Search,
  Filter,
  RefreshCw,
  Phone,
  Mail,
  Building,
  Calendar,
  AlertTriangle,
  CheckCircle,
  FileText,
  Lock,
  ArrowUpDown,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['ALL', 'NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'WON', 'LOST'];
const SCORE_OPTIONS = ['ALL', 'URGENT', 'HIGH', 'MEDIUM', 'LOW'];

const AdminLeads = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem('hoasp_admin_token') || '');
  const [isAuthed, setIsAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [scoreFilter, setScoreFilter] = useState('ALL');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchLeads = async (authToken) => {
    const activeToken = authToken || token;
    if (!activeToken) return;

    setLoading(true);
    try {
      const endpoint = process.env.REACT_APP_BACKEND_URL 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads` 
        : '/api/admin/leads';

      const url = new URL(endpoint, window.location.origin);
      if (statusFilter !== 'ALL') url.searchParams.set('status', statusFilter);
      if (scoreFilter !== 'ALL') url.searchParams.set('score', scoreFilter);
      if (search.trim()) url.searchParams.set('search', search.trim());

      const res = await fetch(url.toString(), {
        headers: {
          'x-admin-token': activeToken
        }
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setLeads(data.leads || []);
        setIsAuthed(true);
        sessionStorage.setItem('hoasp_admin_token', activeToken);
      } else {
        toast.error(data.message || 'Authentication failed');
        setIsAuthed(false);
        sessionStorage.removeItem('hoasp_admin_token');
      }
    } catch (err) {
      console.error('Fetch leads error:', err);
      toast.error('Failed to connect to HOASP admin API');
      setIsAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchLeads(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, scoreFilter]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setToken(tokenInput.trim());
    fetchLeads(tokenInput.trim());
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    if (!selectedLead || !updateStatus) return;

    setUpdating(true);
    try {
      const endpoint = process.env.REACT_APP_BACKEND_URL 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${selectedLead.id}` 
        : `/api/admin/leads/${selectedLead.id}`;

      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': token
        },
        body: JSON.stringify({
          status: updateStatus,
          note: adminNote
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Lead marked as ${updateStatus}`);
        setSelectedLead(data.lead);
        setAdminNote('');
        fetchLeads(token);
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Network error during lead update');
    } finally {
      setUpdating(false);
    }
  };

  const getScoreBadge = (score) => {
    switch (score) {
      case 'URGENT':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400 border border-rose-500/30">URGENT</span>;
      case 'HIGH':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">HIGH</span>;
      case 'MEDIUM':
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">MEDIUM</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-500/20 text-gray-400 border border-gray-500/30">LOW</span>;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      NEW: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      CONTACTED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      QUALIFIED: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      PROPOSAL: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      WON: 'bg-green-500/20 text-green-300 border-green-500/40',
      LOST: 'bg-gray-700/50 text-gray-400 border-gray-600/30'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${colors[status] || colors.NEW}`}>
        {status}
      </span>
    );
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4 pt-24">
        <div className="w-full max-w-md bg-gray-900 border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl text-cyan-400">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">HOASP Admin Gateway</h1>
              <p className="text-xs text-gray-400">Restricted access for authorized personnel</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1.5">
                Admin Secret Key
              </label>
              <input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Enter ADMIN_SECRET_KEY"
                className="w-full bg-gray-950 border border-white/15 focus:border-cyan-400 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-medium rounded-xl shadow-lg transition"
            >
              {loading ? 'Authenticating...' : 'Access Lead Console'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8 pt-24">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-gray-900/60 border border-white/10 p-6 rounded-2xl backdrop-blur-md">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
              <h1 className="text-2xl font-bold">HOASP AI Lead Management</h1>
            </div>
            <p className="text-xs text-gray-400">
              Real-time qualified opportunities captured by the HOASP AI Assistant & contact portals
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchLeads(token)}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-xs font-medium text-gray-200 transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem('hoasp_admin_token');
                setIsAuthed(false);
                setToken('');
              }}
              className="px-3 py-2 bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/20 text-rose-300 rounded-xl text-xs font-medium transition"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-gray-900/40 border border-white/5 p-4 rounded-xl">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchLeads(token)}
              placeholder="Search leads by name, email, phone, organization..."
              className="w-full bg-gray-950 border border-white/10 focus:border-cyan-400 rounded-lg pl-9 pr-4 py-2 text-xs text-white placeholder-gray-500 focus:outline-none"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full bg-gray-950 border border-white/10 focus:border-cyan-400 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              {STATUS_OPTIONS.map((st) => (
                <option key={st} value={st}>Status: {st}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={scoreFilter}
              onChange={(e) => setScoreFilter(e.target.value)}
              className="w-full bg-gray-950 border border-white/10 focus:border-cyan-400 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              {SCORE_OPTIONS.map((sc) => (
                <option key={sc} value={sc}>Score: {sc}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Main Grid: Lead Table + Detail Drawer */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Table */}
          <div className="lg:col-span-2 bg-gray-900/40 border border-white/10 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-900 text-gray-400 uppercase text-[10px] tracking-wider border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Lead & Org</th>
                    <th className="px-3 py-3">Score</th>
                    <th className="px-3 py-3">Requirement</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {leads.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        {loading ? 'Loading leads...' : 'No qualified leads found matching filters.'}
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr
                        key={lead.id}
                        onClick={() => {
                          setSelectedLead(lead);
                          setUpdateStatus(lead.status);
                        }}
                        className={`cursor-pointer transition-colors ${
                          selectedLead?.id === lead.id ? 'bg-cyan-950/40 border-l-2 border-cyan-400' : 'hover:bg-white/5'
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">
                          <div className="text-white font-semibold">{lead.name}</div>
                          <div className="text-gray-400 text-[11px] flex items-center gap-1 mt-0.5">
                            <Building className="w-3 h-3 text-cyan-400" />
                            {lead.organization || 'Individual'}
                          </div>
                        </td>
                        <td className="px-3 py-3">{getScoreBadge(lead.leadScore)}</td>
                        <td className="px-3 py-3 max-w-[200px] truncate text-gray-300">
                          {lead.requirement}
                        </td>
                        <td className="px-3 py-3">{getStatusBadge(lead.status)}</td>
                        <td className="px-4 py-3 text-right text-gray-400 text-[11px]">
                          {new Date(lead.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lead Detail Panel */}
          <div className="bg-gray-900/60 border border-white/10 rounded-2xl p-5 backdrop-blur-md">
            {selectedLead ? (
              <div className="space-y-4 text-xs">
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div>
                    <h2 className="text-base font-bold text-white">{selectedLead.name}</h2>
                    <p className="text-gray-400 text-[11px]">{selectedLead.organization} ({selectedLead.organizationType})</p>
                  </div>
                  {getScoreBadge(selectedLead.leadScore)}
                </div>

                {/* Direct Actions */}
                <div className="grid grid-cols-2 gap-2">
                  {selectedLead.phone && (
                    <a
                      href={`tel:${selectedLead.phone}`}
                      className="flex items-center justify-center gap-1.5 py-2 bg-emerald-950/40 border border-emerald-500/20 text-emerald-300 rounded-lg hover:bg-emerald-900/40 transition"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      Call {selectedLead.phone}
                    </a>
                  )}
                  {selectedLead.email && (
                    <a
                      href={`mailto:${selectedLead.email}`}
                      className="flex items-center justify-center gap-1.5 py-2 bg-cyan-950/40 border border-cyan-500/20 text-cyan-300 rounded-lg hover:bg-cyan-900/40 transition"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Email Lead
                    </a>
                  )}
                </div>

                <div className="space-y-2 bg-gray-950/60 p-3 rounded-xl border border-white/5">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Timeline:</span>
                    <span className="text-white font-medium">{selectedLead.timeline || 'Not specified'}</span>
                  </div>
                  {selectedLead.budget && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Budget:</span>
                      <span className="text-white font-medium">{selectedLead.budget}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">Current Status:</span>
                    {getStatusBadge(selectedLead.status)}
                  </div>
                </div>

                {/* Requirement & Conversation Summary */}
                <div>
                  <h3 className="text-gray-300 font-semibold mb-1">Project Requirement:</h3>
                  <div className="bg-gray-950 p-3 rounded-lg border border-white/5 text-gray-200 leading-relaxed whitespace-pre-wrap">
                    {selectedLead.requirement}
                  </div>
                </div>

                {selectedLead.conversationSummary && (
                  <div>
                    <h3 className="text-gray-300 font-semibold mb-1">Generated Summary:</h3>
                    <div className="bg-gray-950/60 p-3 rounded-lg border border-white/5 text-gray-300 text-[11px] leading-relaxed whitespace-pre-wrap">
                      {selectedLead.conversationSummary}
                    </div>
                  </div>
                )}

                {selectedLead.notes && (
                  <div>
                    <h3 className="text-gray-300 font-semibold mb-1">Admin History:</h3>
                    <div className="bg-gray-950/40 p-2.5 rounded-lg border border-white/5 text-gray-400 text-[11px] whitespace-pre-wrap">
                      {selectedLead.notes}
                    </div>
                  </div>
                )}

                {/* Status Update Form */}
                <form onSubmit={handleUpdateStatus} className="pt-2 border-t border-white/10 space-y-2.5">
                  <label className="block text-gray-300 font-medium">Update Status & Progress:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={updateStatus}
                      onChange={(e) => setUpdateStatus(e.target.value)}
                      className="bg-gray-950 border border-white/15 rounded-lg px-2.5 py-1.5 text-white focus:outline-none focus:border-cyan-400"
                    >
                      {STATUS_OPTIONS.filter(s => s !== 'ALL').map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      disabled={updating}
                      className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg px-3 py-1.5 font-medium transition"
                    >
                      {updating ? 'Saving...' : 'Save Status'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add follow-up notes (e.g. sent quote on 10th)"
                    className="w-full bg-gray-950 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none"
                  />
                </form>
              </div>
            ) : (
              <div className="text-center py-16 text-gray-500 text-xs">
                Select a lead from the list to view requirements, qualification metrics, and update status.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
